import {
  Box,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Radio,
  RadioGroup,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React, { useState } from "react";
import { FieldWrapper } from "../../components/fields/FieldWrapper/FieldWrapper";
import { NotificationFormValues, stopSendingOptions } from "./types";
import { CollapsibleSection } from "./CollapsibleSection";
import {
  CUSTOM_HOURS_DECIMAL_PLACES,
  customHoursFromMinutes,
  minutesFromCustomHours,
} from "./NotificationUtils";

interface AdvancedSettingsProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({
  isOpen,
  onToggle,
}) => {
  const { values, setFieldValue } = useFormikContext<NotificationFormValues>();
  // Keep a string while typing so fractional hours like "2." / "2.5" are not wiped
  // by the controlled numeric form value.
  const [customValueInput, setCustomValueInput] = useState<string | null>(null);

  const handleStopSendingOptionChange = (value: string) => {
    setFieldValue("stopSendingOption", value);
  };

  const handleStopSendingTimeChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = e.target.value;

    if (value === "custom") {
      setFieldValue("stopSendingTimeUnit", "custom");
    } else {
      const option = stopSendingOptions.find((opt) => opt.value === value);
      if (option) {
        if (option.minutes) {
          setFieldValue("stopSendingTimeValue", option.minutes);
          setFieldValue("stopSendingTimeUnit", "minutes");
        } else if (option.hours) {
          setFieldValue("stopSendingTimeValue", option.hours);
          setFieldValue("stopSendingTimeUnit", "hours");
        }
      }
    }
  };

  const commitCustomValueInput = () => {
    if (customValueInput === null) {
      return;
    }

    const parsed = parseFloat(customValueInput);
    if (!Number.isNaN(parsed)) {
      const max = values.stopSendingCustomUnit === "minutes" ? 60 : 24;
      const nextValue =
        values.stopSendingCustomUnit === "minutes"
          ? Math.round(parsed)
          : customHoursFromMinutes(minutesFromCustomHours(parsed));
      setFieldValue(
        "stopSendingCustomValue",
        Math.min(max, Math.max(1, nextValue))
      );
    }
    setCustomValueInput(null);
  };

  return (
    <CollapsibleSection
      title="Advanced Settings"
      subtitle="OPTIONAL SETTINGS FOR PUSH NOTIFICATION DELIVERY"
      isOpen={isOpen}
      onToggle={onToggle}
      bg="gray.50"
      borderless
    >
      <VStack w="100%" alignItems="flex-start" spacing={6} pb={10}>
        <FieldWrapper
          name="stopSendingOption"
          id="stopSendingOption"
          label="Stop Sending After"
          helperText="Set a time limit for undelivered notifications. If a user hasn't received it within this window, the notification will be discarded."
          fcProps={{
            isRequired: true,
          }}
        >
          {() => (
            <RadioGroup
              onChange={handleStopSendingOptionChange}
              value={values.stopSendingOption}
              name="stopSendingOption"
            >
              <VStack alignItems="stretch" spacing={4}>
                <HStack align="flex-start" spacing={8} w="100%" pt={4}>
                  <VStack alignItems="flex-start" spacing={1} flex={1}>
                    <Radio value="anytime">Deliver Anytime (No Expiry)</Radio>
                    <Text fontSize="sm" color="gray.600" pl={6}>
                      Set the notification to deliver without an expiration
                      time.
                    </Text>
                  </VStack>
                  <VStack alignItems="flex-start" spacing={1} flex={1}>
                    <Radio value="specific">Stop After a Specific Time</Radio>
                    <Text fontSize="sm" color="gray.600" pl={6}>
                      Choose when the notification should stop being sent.
                    </Text>
                  </VStack>
                </HStack>

                {values.stopSendingOption === "specific" && (
                  <Box pl={6} mt={2}>
                    <Select
                      value={
                        values.stopSendingTimeUnit === "custom"
                          ? "custom"
                          : stopSendingOptions.find(
                              (o) =>
                                (o.minutes === values.stopSendingTimeValue &&
                                  values.stopSendingTimeUnit === "minutes") ||
                                (o.hours === values.stopSendingTimeValue &&
                                  values.stopSendingTimeUnit === "hours")
                            )?.value || "5min"
                      }
                      onChange={handleStopSendingTimeChange}
                      mb={3}
                    >
                      {stopSendingOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>

                    {values.stopSendingTimeUnit === "custom" && (
                      <HStack spacing={3}>
                        <NumberInput
                          min={1}
                          max={
                            values.stopSendingCustomUnit === "minutes" ? 60 : 24
                          }
                          step={
                            values.stopSendingCustomUnit === "hours" ? 0.5 : 1
                          }
                          precision={
                            values.stopSendingCustomUnit === "hours"
                              ? CUSTOM_HOURS_DECIMAL_PLACES
                              : 0
                          }
                          value={
                            customValueInput ?? values.stopSendingCustomValue
                          }
                          onChange={(valueString, valueAsNumber) => {
                            if (values.stopSendingCustomUnit === "minutes") {
                              setCustomValueInput(null);
                              if (
                                valueString === "" ||
                                Number.isNaN(valueAsNumber)
                              ) {
                                return;
                              }
                              setFieldValue(
                                "stopSendingCustomValue",
                                Math.round(valueAsNumber)
                              );
                              return;
                            }

                            // Hours: keep the typed string so intermediate values
                            // like "2." are preserved until a full number is entered.
                            setCustomValueInput(valueString);
                            if (
                              valueString !== "" &&
                              !valueString.endsWith(".") &&
                              !Number.isNaN(valueAsNumber)
                            ) {
                              setFieldValue(
                                "stopSendingCustomValue",
                                valueAsNumber
                              );
                            }
                          }}
                          onBlur={commitCustomValueInput}
                          w="150px"
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>

                        <Select
                          value={values.stopSendingCustomUnit}
                          onChange={(e) => {
                            const newUnit = e.target.value as
                              | "minutes"
                              | "hours";
                            setCustomValueInput(null);
                            setFieldValue("stopSendingCustomUnit", newUnit);

                            if (newUnit === "minutes") {
                              const nextValue = Math.round(
                                values.stopSendingCustomValue
                              );
                              setFieldValue(
                                "stopSendingCustomValue",
                                Math.min(60, Math.max(1, nextValue))
                              );
                            } else if (values.stopSendingCustomValue > 24) {
                              setFieldValue("stopSendingCustomValue", 24);
                            }
                          }}
                        >
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                        </Select>
                      </HStack>
                    )}
                  </Box>
                )}
              </VStack>
            </RadioGroup>
          )}
        </FieldWrapper>
      </VStack>
    </CollapsibleSection>
  );
};
